<template>
  <Dialog
    v-model:visible="visible"
    header="Customize Dashboard"
    :modal="true"
    :style="{ width: '90vw', maxWidth: '800px' }"
    class="dashboard-customizer-dialog"
  >
    <div class="flex flex-col gap-4">
      <!-- Search -->
      <div class="relative">
        <Icon
          name="mdi:magnify"
          class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          size="18"
        />
        <InputText
          v-model="searchQuery"
          placeholder="Search widgets..."
          class="w-full pl-10"
        />
      </div>

      <!-- Enabled Widgets - Reorder Section -->
      <div
        v-if="enabledWidgetsList.length > 0"
        class="border-b border-gray-200 pb-4"
      >
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-semibold text-gray-700">
            Enabled Widgets ({{ enabledWidgetsList.length }})
          </h4>
          <span class="text-xs text-gray-500">Drag to reorder</span>
        </div>
        <draggable
          v-model="reorderedWidgets"
          item-key="id"
          handle=".drag-handle"
          class="space-y-2"
          :animation="150"
          @end="handleDragEnd"
        >
          <template #item="{ element, index }">
            <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors">
              <div class="drag-handle cursor-grab active:cursor-grabbing text-gray-400 hover:text-primary-500 p-1">
                <Icon
                  name="mdi:drag"
                  size="20"
                />
              </div>
              <div class="w-6 h-6 rounded bg-primary-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {{ index + 1 }}
              </div>
              <span class="text-sm font-medium text-gray-700 flex-1">{{ element.name }}</span>
            </div>
          </template>
        </draggable>
      </div>

      <!-- All Widgets -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-3">
          All Widgets
        </h4>
        <div class="space-y-2 max-h-[400px] overflow-y-auto">
          <div
            v-for="widget in allWidgetsList"
            :key="widget.typeKey"
            class="flex items-center justify-between p-3 rounded-lg border transition-colors"
            :class="[
              getWidgetState(widget.typeKey)?.enabled
                ? 'bg-primary-50 border-primary-300'
                : 'bg-white border-gray-200 hover:border-gray-300',
            ]"
          >
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <InputSwitch
                :model-value="getWidgetState(widget.typeKey)?.enabled ?? false"
                @update:model-value="toggleWidgetByTypeKey(widget.typeKey)"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span
                    class="text-sm font-medium"
                    :class="getWidgetState(widget.typeKey)?.enabled ? 'text-primary-900' : 'text-gray-700'"
                  >
                    {{ widget.name }}
                  </span>
                  <Tag
                    :value="widget.minsize === 1 ? '1' : '2'"
                    :severity="widget.minsize === 1 ? 'success' : 'warning'"
                    size="small"
                  />
                </div>
                <p class="text-xs text-gray-500 mt-0.5 line-clamp-1">
                  {{ widget.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Reset"
          severity="secondary"
          outlined
          size="small"
          @click="handleReset"
        />
        <Button
          label="Done"
          @click="handleDone"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import draggable from 'vuedraggable'
import type { DashboardWidget } from '~/composables/useDashboardLayout'

interface Widget {
  name: string
  description: string
  minsize: 1 | 2
  typeKey: string
}

interface WidgetCategory {
  name: string
  widgets: Widget[]
}

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
}>()

const {
  widgets,
  toggleWidget,
  resetLayout,
  updateWidgetOrder,
  getEnabledWidgets,
} = useDashboardLayout()

const visible = computed({
  get: () => props.visible,
  set: value => emit('update:visible', value),
})

const searchQuery = ref('')
const reorderedWidgets = ref<DashboardWidget[]>([])

// Widget categories
const widgetCategories = ref<WidgetCategory[]>([
  {
    name: 'General',
    widgets: [
      {
        name: 'CA\'s Requiring Qualification',
        description: 'Displays businesses with CA\'s that require qualification and shows the number of completed CA\'s awaiting approval.',
        minsize: 1,
        typeKey: 'ca',
      },
      {
        name: 'Admin Noticeboard',
        description: 'Shows important announcements and notices for administrators and team members.',
        minsize: 1,
        typeKey: 'noticeboard',
      },
      {
        name: 'Team Chat',
        description: 'Real-time team communication widget for quick messaging and collaboration.',
        minsize: 1,
        typeKey: 'team-chat',
      },
      {
        name: 'Upcoming Events',
        description: 'Displays scheduled events, meetings, and important dates for the team.',
        minsize: 1,
        typeKey: 'upcoming-events',
      },
      {
        name: 'My Tasks',
        description: 'Shows personal tasks assigned to you with status, due dates, and related business or contact information.',
        minsize: 1,
        typeKey: 'tasks',
      },
      {
        name: 'CIM Analytics',
        description: 'Analytics and insights related to Confidential Information Memorandums (CIM).',
        minsize: 2,
        typeKey: 'cim-analytics',
      },
      {
        name: 'Business Timeline',
        description: 'Activity timeline tracking all key events, notes, tasks, and updates for businesses.',
        minsize: 2,
        typeKey: 'business-timeline',
      },
    ],
  },
  {
    name: 'Business Analytics',
    widgets: [
      {
        name: 'Pipeline Status Chart',
        description: 'Visual representation of businesses in different pipeline stages (e.g., Active, Sold, On Hold).',
        minsize: 2,
        typeKey: 'pipeline-status',
      },
      {
        name: 'Document Status Summary',
        description: 'Overview summary of document completion status across all businesses.',
        minsize: 1,
        typeKey: 'doc-status-summary',
      },
      {
        name: 'Document Status Chart',
        description: 'Detailed chart showing the status of various documents for businesses.',
        minsize: 2,
        typeKey: 'doc-status-chart',
      },
      {
        name: 'New Businesses by Month',
        description: 'Chart displaying the number of new businesses added each month over time.',
        minsize: 2,
        typeKey: 'new-businesses-month',
      },
      {
        name: 'Sold Businesses by Month',
        description: 'Chart showing the number of businesses sold each month, tracking sales trends.',
        minsize: 2,
        typeKey: 'sold-businesses-month',
      },
      {
        name: 'Businesses by Agent',
        description: 'Chart or visualization showing the distribution of businesses assigned to different agents.',
        minsize: 2,
        typeKey: 'businesses-by-agent',
      },
    ],
  },
  {
    name: 'Commission Analytics',
    widgets: [
      {
        name: 'Commission Summary',
        description: 'Overview of total commissions, averages, and key commission metrics.',
        minsize: 1,
        typeKey: 'commission-summary',
      },
      {
        name: 'Commission by Agent',
        description: 'Chart displaying commission amounts earned by each agent.',
        minsize: 2,
        typeKey: 'commission-by-agent',
      },
      {
        name: 'Commission by Month',
        description: 'Trend chart showing commission earnings over time, broken down by month.',
        minsize: 2,
        typeKey: 'commission-by-month',
      },
      {
        name: 'Commission by Stage',
        description: 'Visualization of commissions categorized by business pipeline stage.',
        minsize: 2,
        typeKey: 'commission-by-stage',
      },
      {
        name: 'Commission by Status',
        description: 'Chart showing commission distribution based on business or transaction status.',
        minsize: 2,
        typeKey: 'commission-by-status',
      },
      {
        name: 'Office vs Agent Breakdown',
        description: 'Comparison chart showing commission distribution between office and individual agents.',
        minsize: 2,
        typeKey: 'office-vs-agent',
      },
    ],
  },
  {
    name: 'Industry Analytics',
    widgets: [
      {
        name: 'Industry Summary',
        description: 'Overview of key metrics and statistics across different industries.',
        minsize: 1,
        typeKey: 'industry-summary',
      },
      {
        name: 'Total Businesses Chart',
        description: 'Chart showing the total number of businesses by industry.',
        minsize: 2,
        typeKey: 'total-businesses-chart',
      },
      {
        name: 'Total Price Chart',
        description: 'Visualization of total business values or prices by industry.',
        minsize: 2,
        typeKey: 'total-price-chart',
      },
      {
        name: 'Average Price Chart',
        description: 'Chart displaying the average business price or value per industry.',
        minsize: 2,
        typeKey: 'average-price-chart',
      },
      {
        name: 'Average Multiple Chart',
        description: 'Chart showing average valuation multiples by industry sector.',
        minsize: 2,
        typeKey: 'average-multiple-chart',
      },
    ],
  },
])

// All widgets flattened and filtered by search
const allWidgetsList = computed(() => {
  let allWidgets = widgetCategories.value.flatMap(cat => cat.widgets)

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    allWidgets = allWidgets.filter(widget =>
      widget.name.toLowerCase().includes(query)
      || widget.description.toLowerCase().includes(query),
    )
  }

  return allWidgets
})

// Enabled widgets for reordering
const enabledWidgetsList = computed(() => {
  return getEnabledWidgets.value
})

// Helper functions
const getWidgetState = (typeKey: string): DashboardWidget | undefined => {
  return widgets.value.find(w => w.id === typeKey)
}

const toggleWidgetByTypeKey = (typeKey: string) => {
  toggleWidget(typeKey)
}

// Watch for widget changes to update reordered list
watch(() => getEnabledWidgets.value, (newWidgets) => {
  const currentOrder = reorderedWidgets.value.map(w => w.id)
  const newOrder = newWidgets
    .sort((a, b) => {
      const aIndex = currentOrder.indexOf(a.id)
      const bIndex = currentOrder.indexOf(b.id)
      if (aIndex === -1 && bIndex === -1) return a.order - b.order
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1
      return aIndex - bIndex
    })
  reorderedWidgets.value = newOrder
}, { immediate: true })

const handleDragEnd = () => {
  updateWidgetOrder(reorderedWidgets.value)
}

const handleReset = () => {
  if (confirm('Reset dashboard to default layout?')) {
    resetLayout()
  }
}

const handleDone = () => {
  visible.value = false
}
</script>

<style scoped>
.dashboard-customizer-dialog :deep(.p-dialog-content) {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}
</style>
